import { inputClasses } from '@mui/material';
import { VyTooltip } from '../../../../../../components/new-design/VyTooltip';
import type { UsageProductInput } from '@vy/types';
import { isNotNullOrUndefined, withDefault } from '@vy/utils';
import type { ComponentProps } from 'react';
import { useRef, useState } from 'react';
import type { FieldPath } from 'react-hook-form';
import { get, useFormContext } from 'react-hook-form';
import { VayuTextField } from '../../../../../../components/design/vayu-theme-components/VayuTextField';
import { useFormErrorNotificationContext } from '../../../../../../contexts/form-error-notification-context/form-error-notification-context';
import { useIsOverflowed } from '../../../../../../hooks/use-is-overflowed';
import type { NewPlanForm } from '../../CreatePlanForm/create-plan-form-types/new-plan-form';
import type { PlanInputForm } from '../../CreatePlanForm/create-plan-form-types/plan-input-form';
import type { CreatePlanTab } from '../../CreatePlanPageTabs/create-plan-page-types/create-plan-tab';
import { CREATE_PLAN_TABS } from '../../CreatePlanPageTabs/create-plan-page-types/create-plan-tab';

type MeteredCreditEditCellProps = {
  meteredCredit: UsageProductInput;
  field: FieldPath<UsageProductInput>;
  valueAsNumber?: boolean;
} & ComponentProps<typeof VayuTextField>;

export const MeteredCreditEditCell: React.FC<MeteredCreditEditCellProps> = ({
  meteredCredit,
  field,
  valueAsNumber = false,
  ...props
}) => {
  const { registerErrorField } = useFormErrorNotificationContext<PlanInputForm, CreatePlanTab>();

  const {
    register,
    getValues,
    formState: { errors },
  } = useFormContext<PlanInputForm, NewPlanForm>();

  const usageProducts = withDefault(getValues('usageProducts'), []);

  const meteredCreditIndex = usageProducts.findIndex((usageProduct) => usageProduct.id === meteredCredit.id);

  const nameAdjustedField = field === 'name' ? 'variants.0.name' : field;

  const registerField = register(`usageProducts.${meteredCreditIndex}.${nameAdjustedField}`, {
    valueAsNumber,
  });

  registerErrorField(registerField.name, CREATE_PLAN_TABS.promotions);

  const [isEditing, setIsEditing] = useState(false);
  const [isHovering, setIsHovering] = useState(false);

  const editCellRef = useRef<HTMLInputElement>(null);
  const isInputOverflowed = useIsOverflowed(editCellRef);

  return (
        <VyTooltip
      tooltipContent={getValues(`usageProducts.${meteredCreditIndex}.${field}`)?.toString() ?? ''}
      placement="top-start"
      arrow
      open={!isEditing && isHovering && isInputOverflowed}
    >
      <VayuTextField
        {...registerField}
        onKeyDown={(event) => event.stopPropagation()}
        sx={{
          ...props.sx,
          [`& .${inputClasses.root}`]: {
            border: 'none',
            ['&.Mui-focused']: {
              border: '1px solid #E1E6E8',
              padding: '4px 16px !important',
            },
          },
        }}
        inputRef={editCellRef}
        {...props}
        onFocus={(event) => {
          setIsEditing(true);
          props.onFocus?.(event);
        }}
        onBlur={(event) => {
          setIsEditing(false);
          props.onBlur?.(event);
        }}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        error={isNotNullOrUndefined(get(errors, registerField.name))}
      />
    </VyTooltip>
  );
};
